import { GameState } from '../store/gameStore';

interface CacheEntry {
  value: number;
  timestamp: number;
  key: string;
}

export class OptimizedCalculations {
  private static instance: OptimizedCalculations;
  private cache: Map<string, CacheEntry> = new Map();
  private cacheTimeout = 5000; // 5 Sekunden Cache

  static getInstance(): OptimizedCalculations {
    if (!OptimizedCalculations.instance) {
      OptimizedCalculations.instance = new OptimizedCalculations();
    }
    return OptimizedCalculations.instance;
  }

  private getCacheKey(prefix: string, state: GameState): string {
    const activeCompanionIds = state.companions.active?.map(c => c?.id).filter(Boolean).join(',') || 'none';
    return `${prefix}_${state.playerLevel}_${state.autoClickPower}_${state.powerUps.goldRush.isActive ? 1 : 0}_${state.powerUps.speedBoost.isActive ? 1 : 0}_${activeCompanionIds}`;
  }

  private isCacheValid(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp < this.cacheTimeout;
  }

  calculateCoinsPerSecond(state: GameState): number {
    const cacheKey = this.getCacheKey('coinsPerSecond', state);
    const cached = this.cache.get(cacheKey);
    
    if (cached && this.isCacheValid(cached)) {
      return cached.value;
    }

    // Optimierte Berechnung für hohe Level
    let total = state.autoClickPower;
    
    // Basis-Berechnungen (immer)
    const autoClickerEfficiency = 1 + (state.prestige?.permanentBonuses?.autoClickerEfficiency || 0) * 0.3;
    total *= autoClickerEfficiency;
    
    // Gartenbereiche (nur wichtigste für hohe Level)
    if (state.playerLevel < 5000) {
      Object.keys(state.gardenAreas).forEach(areaKey => {
        const area = areaKey as keyof typeof state.gardenAreas;
        total += this.getAreaBonus(state, area) / 60;
      });
    } else {
      // Nur die wichtigsten Bereiche für hohe Level
      const keyAreas = ['goldMine', 'treasureVault', 'alchemyLab', 'magicForge'];
      keyAreas.forEach(areaKey => {
        const area = areaKey as keyof typeof state.gardenAreas;
        if (state.gardenAreas[area]) {
          total += this.getAreaBonus(state, area) / 60;
        }
      });
    }
    
    // Power-Ups
    if (state.powerUps.speedBoost.isActive) total *= 2;
    if (state.powerUps.timeWarp.isActive) total *= 10;
    if (state.powerUps.gardenBlessing.isActive) total *= 3;
    if (state.powerUps.clickFrenzy.isActive) total *= 5;
    if (state.powerUps.cosmicBoost.isActive) total *= 10;
    if (state.powerUps.dragonPower.isActive) total *= 25;
    
    // Begleiter-Boni (nur wenn Begleiter vorhanden)
    if (state.companions.active && state.companions.active.length > 0) {
      const companionCoinPerSecond = this.getCompanionBonus(state, 'coinPerSecond');
      if (companionCoinPerSecond > 0) {
        total += companionCoinPerSecond;
      }
      
      const companionAutoClickBonus = this.getCompanionBonus(state, 'autoClickPower');
      if (companionAutoClickBonus > 0) {
        total += companionAutoClickBonus;
      }
    }
    
    // Cache speichern
    this.cache.set(cacheKey, {
      value: total,
      timestamp: Date.now(),
      key: cacheKey
    });
    
    return total;
  }

  calculateClickMultiplier(state: GameState): number {
    const cacheKey = this.getCacheKey('clickMultiplier', state);
    const cached = this.cache.get(cacheKey);
    
    if (cached && this.isCacheValid(cached)) {
      return cached.value;
    }

    let multiplier = 1;
    
    // Level-Bonus (optimiert für hohe Level)
    let levelBonus = 0;
    if (state.playerLevel >= 1000) {
      levelBonus = (state.playerLevel - 1) * 1;
    }
    multiplier += levelBonus;
    
    // Power-Ups
    if (state.powerUps.megaMultiplier.isActive) multiplier *= 5;
    if (state.powerUps.infiniteEnergy.isActive) multiplier *= 2;
    if (state.powerUps.cosmicBoost.isActive) multiplier *= 10;
    if (state.powerUps.dragonPower.isActive) multiplier *= 25;
    
    // Begleiter-Boni
    const companionClickBonus = this.getCompanionBonus(state, 'clickMultiplier');
    if (companionClickBonus > 0) {
      multiplier += companionClickBonus / 100; // Prozent zu Dezimal
    }
    
    const finalMultiplier = Math.max(1, multiplier);
    
    // Cache speichern
    this.cache.set(cacheKey, {
      value: finalMultiplier,
      timestamp: Date.now(),
      key: cacheKey
    });
    
    return finalMultiplier;
  }

  private getAreaBonus(state: GameState, area: keyof GameState['gardenAreas']): number {
    const areaData = state.gardenAreas[area];
    if (!areaData) return 0;
    
    // Optimierte Bonus-Berechnung
    const baseBonus = areaData.level * 2;
    const efficiency = 1 + (state.prestige?.permanentBonuses?.gardenEfficiency || 0) * 0.2;
    
    return baseBonus * efficiency;
  }

  private getCompanionBonus(state: GameState, abilityType: string): number {
    try {
      const activeCompanions = state.companions?.active;
      
      if (!activeCompanions || !Array.isArray(activeCompanions) || activeCompanions.length === 0) {
        return 0;
      }
      
      // Summiere alle Boni der aktiven Begleiter
      let totalBonus = 0;
      activeCompanions.forEach(companion => {
        if (companion && companion.abilities && Array.isArray(companion.abilities)) {
          const ability = companion.abilities.find(a => a && a.type === abilityType);
          if (ability && typeof ability.value === 'number' && !isNaN(ability.value)) {
            let abilityValue = ability.value;
            
            // Kampf-Level verstärkt Standard-Fähigkeiten (nicht Kampf-Fähigkeiten)
            const isStandardAbility = !['criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'].includes(abilityType);
            if (isStandardAbility && companion.battleLevel && typeof companion.battleLevel === 'number' && companion.battleLevel > 1 && !isNaN(companion.battleLevel)) {
              // Jedes Kampf-Level über 1 gibt +10% Bonus auf Standard-Fähigkeiten
              const battleLevelMultiplier = 1 + ((companion.battleLevel - 1) * 0.1);
              abilityValue = Math.floor(abilityValue * battleLevelMultiplier);
            }
            
            // Validierung vor Addition
            if (!isNaN(abilityValue) && isFinite(abilityValue)) {
              totalBonus += abilityValue;
            }
          }
        }
      });
      
      // Finale Validierung
      if (isNaN(totalBonus) || !isFinite(totalBonus)) {
        console.warn('NaN oder unendlicher Wert in getCompanionBonus, verwende Fallback');
        return 0;
      }
      
      return totalBonus;
    } catch (error) {
      console.error('Fehler in getCompanionBonus:', error);
      return 0; // Fallback-Wert
    }
  }

  clearCache(): void {
    this.cache.clear();
  }

  getCacheStats(): { size: number; hitRate: number } {
    return {
      size: this.cache.size,
      hitRate: 0.8 // Schätzung
    };
  }
} 